import axios from 'axios';
import PromiseProxy from './PromiseProxy'
import LongPolling from './LongPolling'

/**
 * ルーム関連
 */
export default class Api {

  constructor (authToken = null) {
    this.authToken = authToken;
    this._axiosInstance = null;
    this._sessionExpiredHandler = null;
    this._roomToken = null;
  }

  set onSessionExpired (newHandler) {
    this._sessionExpiredHandler = newHandler;
  }

  set roomAccessToken (newToken) {
    // axiosインスタンスのヘッダー設定を更新するため破棄する。
    this._axiosInstance = null;
    this._roomToken = newToken;
  }

  /*
   * ログイン
   */
  login (userId, password) {
    let login = this.request('POST', '/login', {
      data: {
        user_id: userId,
        passwd: password
      }
    })
    return login
  }

  /*
   * ログアウト
   */
  logout () {
    return this.request('POST', '/logout')
  }

  /*
   * オペレーター状態更新
   */
  updateOpState (newState) {
    return this.request('PUT', '/operator/state', {
      data: newState
    })
  }

  /*
   * オペレーター一覧取得
   */
  getOperators () {
    return this.request('GET', '/operators')
  }

  /*
  * チャット対応可能なオペレータがいるかをステータスコードで返却
  */
  getSeatedOperators (roomId) {
    return this.request('GET', `/operators/seated/${roomId}`)
  }

  /*
   * ルーム一覧取得
   */
  getRooms (params) {
    let req_params = {}
    if (params.todayOnly) {
      req_params['today_only'] = 1
    }
    if (params.minDate) {
      req_params['min_date'] = params.minDate
    }
    if (params.maxDate) {
      req_params['max_date'] = params.maxDate
    }
    if (params.pageNum) {
      req_params['page_num'] = params.pageNum
    }
    if (params.pageRowCount) {
      req_params['page_row_count'] = params.pageRowCount
    }

    return this.request('GET', '/rooms', {
      params: req_params
    })
  }

  /*
   * id配列によるルーム一覧取得
   */
  getRoomsById (ids) {
    return this.request('GET', '/rooms', {
      params: {
        ids: ids.join(',')
      }
    })
  }

  /*
   * ログイン中オペレーターが対応中のルーム一覧取得
   */
  getOpRespondingRooms () {
    return this.request('GET', '/rooms/op-responding')
  }

  /*
   * ユーザーがアクセスしたルームの状態を取得
   */
  currentRoomStatus (roomId) {
      return this.request('GET', `/rooms/state/${roomId}`)
    }
  
  /**
   * 新規ルーム作成
   */
  createRoom (isEmail = false) {
    return this.request('POST', '/rooms',{
      data: { is_email_chat: isEmail }
    });;
  }

  /**
     * 既存ルーム取得
     *
     * @param roomId 取得するルームID
     * @param accessToken ルーム認証トークン
     */
    getRoom (roomId) {
    return this.request('GET', `/rooms/${roomId}`);
    }

  /**
   * ルームにBotを割り当て
   */
  attachBot (roomProps) {

    let url = process.env.CHATSERVICE_URL + '/bot/instance';
    return axios.post(url, roomProps);
  }

  /**
   * チャットルーム内のメッセージ取得
   */
  getRoomMessages (roomId, sinceId) {

    let params = {};
    if (sinceId !== null) {
      params['since_id'] = sinceId;
    }

    return this.request('GET', `/rooms/${roomId}/messages`, {
      params: params
    });
  }

  /**
   * チャットルームへメッセージ送信
   */
  sendRoomMessage (roomId, message) {

    return this.request('POST', `/rooms/${roomId}/messages`, {
      data: message,
      headers: {
        'content-type': 'application/json; charset=UTF-8',
        'accept': 'application/json; charset=UTF-8'
      }
    });
  }

  /**
   * 既読更新
   */
  updateReadDisplay (roomId) {
    let params = {};
    params['type'] = 'user';

    return this.request('POST', `/rooms/${roomId}/update-reading-flg`, {
      params: params
    });
  }

  /**
   * 既読情報の取得
   */
  getReadMessages (roomId, type) {
    return new LongPolling(this, `/rooms/${roomId}/get-read-message/${type}`)
  }

  /**
   * 未読表示用新着メッセージ取得
   */
  unreadsendMessage (roomId) {
      return this.request('POST', `/room/${roomId}/unread-message`);
    }

  /*
   * OP・SV間チャットメッセージ取得
   */
  getStaffMessagesLongPolling (roomId) {
    return new LongPolling(this, `/rooms/${roomId}/staff-messages`)
  }

  /*
   * OP・SV間チャットメッセージ送信
   */
  sendStaffMessage (roomId, message) {
    return this.request('POST', `/rooms/${roomId}/staff-messages`, {
      data: { message }
    })
  }

  /*
   * チャットルーム内にファイル送信
   */
  sendRoomFile (roomId, file) {
    let formData = new FormData()
    formData.append('file', file)

    return this.request('POST', `/rooms/${roomId}/files`, {
      data: formData
    })
  }

  /*
   * チャットルーム内のファイル内容取得URL
   */
  getRoomFileURL (roomId, fileId, accessToken) {
    // let basePath = `${process.env.CHATSERVICE_URL}/api`
    let basePath = 'https://chat-cs.regza.com/api'
    return `${basePath}/rooms/${roomId}/files/${fileId}?key=${accessToken}`
  }

  /*
   * ルーム状態更新
   */
  updateRoomState (roomId, newState, operatorId) {
    let data = { state: newState }
    if (operatorId !== undefined) {
      data['operator_id'] = operatorId
    }
    return this.request('PUT', `/rooms/${roomId}/state`, {
      data: data
    })
  }

  /*
   * Bot応答不能・SV呼出中のルーム状態レコード取得
   */
  getWarningRoomStatesLongPolling () {
    return new LongPolling(this, '/room/warning-states');
  }

  /*
   * ルーム担当割当て
   */
  assignRoom (roomId, operatorId) {
    return this.request('POST', '/room/assigns', {
      data: {
        'room_id': roomId,
        'operator_id': operatorId
      }
    })
  }

  /*
   * ルーム担当割当てを承諾
   */
  acceptRoomAssign (assignId) {
    return this.updateRoomAssign(assignId, true)
  }

  /*
   * ルーム担当割当てを拒否
   */
  rejectRoomAssign (assignId) {
    return this.updateRoomAssign(assignId, false)
  }

  /*
   * ルーム担当割当てを承諾/拒否
   */
  updateRoomAssign (assignId, accepted) {
    return this.request('PUT', `/room/assigns/${assignId}`, {
      data: { accepted }
    })
  }

  /*
   * 自分に対するルーム割当て取得
   */
  getMyRoomAssigns (sinceId) {
    let params = {}
    if (sinceId) params['since_id'] = sinceId
    return this.request('GET', '/room/my-assigns', { params })
  }

  /*
   * 自分に対するルーム割当て取得 (ロングポーリング)
   */
  getMyRoomAssignsLongPolling () {
    return new LongPolling(this, '/room/my-assigns')
  }

  /*
   * 単一の質問＆回答候補取得
   */
  getInquiry (qid) {
    return this.request('GET', `/inquiries/${qid}`)
  }

  /*
   * 質問＆回答候補検索
   */
  searchInquiries (productId, searchWord) {
    return this.request('GET', '/inquiries', {
      params: {
        product_id: productId,
        word: searchWord
      }
    })
  }

  /*
   * 回答に対する次の質問紐付け
   */
  setNextInquiry (aid, nextQid) {
    return this.request('PUT', `/inquiries/a/${aid}/child`, {
      data: nextQid
    })
  }

  /*
   * 質問＆回答候補新規作成
   */
  createInquiry (params) {
    return this.request('POST', '/inquiries', {
      data: {
        product_id: params.productId,
        text: params.text,
        terminal: params.terminal,
        root: params.root,
        tree_name: params.treeName,
        repair: params.repair,
        repair_cost_num: params.repairCostNum,
        alt_product_id: params.altProductId,
        answers: params.answers
      }
    })
  }

  /*
   * ユーザー一覧取得
   */
  getUsers () {
    return this.request('GET', '/users')
  }

  /*
   *  ユーザー作成
   */
  createUser (newUser) {
    return this.request('POST', '/users', {
      data: newUser
    })
  }

  /**
   * ユーザー取得
   *
   * @param id ユーザーID (PKもしくはメールアドレス)
   */
  getUser (id) {
    return this.request('GET', `/users/${id}`)
  }

  /**
   * ユーザー更新
   *
   * @param id ユーザーID (PKもしくはメールアドレス)
   * @param data 更新データ
   */
  updateUser (id, data) {
    return this.request('PUT', `/users/${id}`, {
      data: data
    })
  }

  /**
   * ユーザー削除
   *
   * @param id ユーザーID (PKもしくはメールアドレス)
   */
  deleteUser (id) {
    return this.request('DELETE', `/users/${id}`)
  }

  /**
   * エンドユーザー作成
   */
  createEndUser (params) {
    return this.request('POST', '/end-users', {
      data: {
        user_id:           params.userId,
        passwd:            params.password,
        type:              'user',
        first_name:        params.firstName,
        first_name_kana:   params.firstNameKana,
        last_name:         params.lastName,
        last_name_kana:    params.lastNameKana,
        owned_product_ids: params.ownedProductIds
      }
    })
  }

  /**
   * エンドユーザープロファイル取得
   */
  getEndUserProfile () {
    return this.request('GET', '/end-user/profile')
  }

  /**
   * エンドユーザープロファイル更新
   */
  updateEndUserProfile (params) {
    return this.request('PUT', '/end-user/profile', {
      data: {
        first_name:        params.firstName,
        first_name_kana:   params.firstNameKana,
        last_name:         params.lastName,
        last_name_kana:    params.lastNameKana,
        owned_product_ids: params.ownedProductIds
      }
    })
  }

  /**
   * エンドユーザーパスワード変更
   */
  updateEndUserPassword (params) {
    return this.request('PUT', '/end-user/password', {
      data: {
        current: params.currentPassword,
        new: params.newPassword
      }
    })
  }

  /*
   * 修理申込み作成
   */
  createRepairRequest (data) {
    return this.request('POST', '/repair/requests', {
      data: data
    })
  }

  /*
   * 修理申込み一覧取得
   */
  getRepairRequests (minDate, maxDate) {
    return this.request('GET', '/repair/requests', {
      params: {
        'min_date': minDate,
        'max_date': maxDate
      }
    })
  }

  /**
   * マスター取得 (プロパティ定義+レコード)
   */
  getMaster (name) {
    return this.request('GET', `/master/${name}`)
  }

  /**
   * マスタープロパティ定義取得
   */
  getMasterProps (name) {
    return this.request('GET', `/master/${name}/props`)
  }

  /**
   * マスターレコード取得
   */
  getMasterRecords (name, params = null) {
    let conf = {}
    if (params) {
      conf['params'] = params
    }
    return this.request('GET', `/master/${name}/records`, conf)
  }

  /**
   * マスターレコード更新
   */
  updateMasterRecords (name, records) {
    return this.request('PUT', `/master/${name}/records`, {
      data: records
    })
  }

  /**
   * 仕様マスター取得(機種別プロパティ定義+レコード)
   */
  getSpecMaster (kindId) {
    return this.request('GET', `/master/spec/${kindId}`)
  }

  /**
   * 仕様マスター機種別プロパティ定義取得
   */
  getSpecMasterProps (kindId) {
    return this.request('GET', `/master/spec/${kindId}/props`)
  }

  /**
   * 仕様マスター機種別レコード取得
   */
  getSpecMasterRecords (kindId) {
    return this.request('GET', `/master/spec/${kindId}/records`)
  }

  /**
   * 仕様マスター機種別レコード更新
   */
  updateSpecMasterRecords (kindId, records) {
    return this.request('PUT', `/master/spec/${kindId}/records`, {
      data: records
    })
  }

  /**
   * FAQ作成
   *
   * ■送信パラメーター
   * ・title : タイトル (必須)
   * ・content : マークアップを含む内容 (必須)
   * ・kindId : 機種ID (オプショナル)
   * ・productId : 製品型番 (オプショナル)
   */
  createFAQ (data) {
    return this.request('POST', '/faq', {
      data: {
        title: data.title,
        content: data.content,
        kind_id: data.kindId,
        product_id: data.productId
      }
    })
  }

  /*
   * FAQ検索
   */
  searchFAQ (condition) {
    let params = {
      word: condition.word,
      kind_id: condition.kindId,
      product_id: condition.productId
    }
    return this.request('GET', '/faq', {
      params: params
    })
  }

  /*
   * FAQ取得
   */
  getFAQ (id) {
    return this.request('GET', `/faq/${id}`)
  }

  /*
   * FAQ更新
   *
   * ※送信パラメーターは createFAQ と同じ
   */
  updateFAQ (id, data) {
    return this.request('PUT', `/faq/${id}`, {
      data: {
        title: data.title,
        content: data.content,
        kind_id: data.kindId,
        product_id: data.productId
      }
    })
  }

  /*
   * FAQ削除
   */
  deleteFAQ (id) {
    return this.request('DELETE', `/faq/${id}`)
  }

  /*
   * 検索語に対し解決したFAQとして登録
   */
  registerResolvedFAQ (searchWord, id) {
    return this.request('POST', '/faq/resolved', {
      data: {
        search_word: searchWord,
        faq_id: id
      }
    })
  }

  /*
     * APIリクエストを送信します。
     */
  request (method, url, exConfig = null) {

    let config = {
      method: method,
      url: url
    }
    if (exConfig) {
      for (let key in exConfig) {
        config[key] = exConfig[key];
      }
    }

    let promise = this.axiosInstance.request(config);
    return new PromiseProxy(promise, this._sessionExpiredHandler);
  }

  /*
     * 共通設定を定義したaxiosインスタンスを遅延初期化します。
     */
  get axiosInstance () {
    if (!this._axiosInstance) {
      let opts = {
        // baseURL: process.env.CHATSERVICE_URL + '/api',
        baseURL: 'https://chat-cs.regza.com/api',
        headers: {
          'Authorization': this.authToken ? this.authToken : null,
          'X-Room-Authorization': this._roomToken ? this._roomToken : null
        }
      };
      this._axiosInstance = axios.create(opts);
    }
    return this._axiosInstance;
  }

  /*
  * 現在時刻が営業時間内か外か判定
  */
  getBusinessHours () {
    return this.request('GET', `/room/business-hours`)
  }

  /*
  * チャットメッセージ入力中か判定
  */
  enteringCheck (roomId) {
    return this.request('GET', `/room/entering/${roomId}`)
  }

  /*
  * 在席時間通知
  */
  postSittingTime (roomId) {
    return this.request('POST', `/room/notice-sittingTime/${roomId}`)
  }

  /*
  * roomsテーブル入力中status更新
  */
  userInputCheck (roomId) {
    return this.request('POST', `/room/${roomId}/entering-user`)
  }
}



// WEBPACK FOOTER //
// ./app-common-vue/src/lib/Api.js